import { useContext, useEffect, useMemo, useState, useCallback } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';

import { useBooleanState } from '@vyce/core/src/hooks';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import {
  getDidntClockedOutWorkersRequest,
  getTimeLogsByShiftRequest,
  getDidntClockOutRolesRequest,
  getCompaniesWorkersWidgetSharedData,
} from '@vyce/core/src/api/time';
import { formatTimeSortModel } from '@vyce/core/src/utils/sorting';
import { CheckOutWorkerDTO } from '@vyce/core/src/api/types';
import { useInterval } from '@vyce/core/src/hooks/useInterval';
import { TIME_INTERFACE_PERMISSIONS, TimeLogByShiftDateItem } from '@vyce/core/src/types';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import { useTabFocus } from '@vyce/core/src/hooks/useTabFocus';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { formatDate, generateExcelFileLink, getYesterday } from '@vyce/core/src/utils';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';

import { Props } from '../types';
import { useMainDashboardFilter } from '../../../../hooks/useMainDashboardFilter';
import { AxiosError } from 'axios';

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'desc' }];

export const useWidgetData = ({ selectedCompanyId, mainDashboardFilters, onForbidden }: Props) => {
  const { canAnywhere } = usePermissions();
  const [dateOfTimeLog, setDateOfTimeLog] = useState(getYesterday());
  const [selectedWorker, setSelectedWorker] = useState<CheckOutWorkerDTO | null>(null);
  const [isSmallView, setSmallView] = useState(false);
  const [downloadButtonLoading, setDownloadButtonLoadingTrue, setDownloadButtonLoadingFalse] =
    useBooleanState(false);
  const [isShowSearchField, setShowSearchFieldTrue] = useBooleanState(false);
  const [roleOptions, setRoleOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [filters, setFilters] = useState<{ roles: string[] } | null>(null);
  const [logByShift, setLogByShift] = useState<TimeLogByShiftDateItem | null>(null);
  const [
    isExpandTimeLogsInformationDialog,
    openExpandTimeLogsInformationDialog,
    closeExpandTimeLogsInformationDialog,
  ] = useBooleanState(false);
  const [isEditSingleTimeLogDialogOpen, openEditSingleTimeLogDialog, closeEditSingleTimeLogDialog] =
    useBooleanState(false);
  const [isFilterDialogOpen, openFilterDialog, closeFilterDialog] = useBooleanState(false);
  const { handleServerError } = useContext(NotificationContext);
  // Loading the widget: a 403 hides it instead of raising a toast.
  const handleWidgetError = useWidgetErrorHandler(onForbidden);

  const { isMobile } = useContext(DeviceContext);
  const isMounted = useIsMounted();

  const methods = useForm<{ roles: Record<string, boolean> }>({
    defaultValues: { roles: {} },
  });

  const { isTabFocused } = useTabFocus();
  const {
    sortModel,
    total,
    substring,
    setTotal,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({
    defaultSortModel,
  });

  const {
    isLoadingList,
    data,
    setNewData,
    setInitialLoadingStatusesFalse,
    setInitialLoadingStatusesTrue,
    resetLoadingList,
  } = useInfiniteScroll<CheckOutWorkerDTO>([]);

  const { selectedLocationOptions, unselectedLocationOptions, getLocationsForRequest } =
    useMainDashboardFilter({
      mainDashboardFilters,
    });

  const roles = methods.watch('roles');

  const isFilterWasActivated = Object.entries(roles).some(item => item[1] === false);

  // Held per-location for a scoped manager, so a company-only check would
  // blank the widget for them.
  const cnaViewWorker = useMemo(() => canAnywhere(TIME_INTERFACE_PERMISSIONS.USERS), [canAnywhere]);

  const filterSettings = useMemo(
    () => [
      {
        options: roleOptions,
        title: 'Which roles should be displayed?',
        type: 'roles',
      },
    ],
    [roleOptions]
  );

  const restoreFilters = () => {
    const data = methods.getValues();
    const restoredRoles = Object.keys(data.roles).reduce((sum, curr) => {
      sum[curr] = true;
      return sum;
    }, {} as Record<string, boolean>);

    methods.reset({ roles: restoredRoles });
    handleChangeFilter({ roles: restoredRoles });
  };

  const getTimeLogsByShift = useCallback(
    async ({ userId, date, site_id }: { userId: string; date: string; site_id: string }) => {
      if (!selectedCompanyId) {
        return;
      }
      try {
        setLoadingTrue();
        const { data } = await getTimeLogsByShiftRequest(selectedCompanyId, userId, {
          start_date: date,
          end_date: date,
          site_id,
        });
        if (!isMounted.current) return;
        if (data.date_items.length && data.date_items[0].time_logs.length > 1) {
          openExpandTimeLogsInformationDialog();
        } else {
          openEditSingleTimeLogDialog();
        }
        setLogByShift(data.date_items[0]);

        setLoadingFalse();
      } catch (e) {
        if (isMounted.current) setLoadingFalse();
        console.error(e);
      }
    },
    [selectedCompanyId]
  );

  const getRoles = async () => {
    if (!selectedCompanyId) {
      return;
    }
    try {
      const { data } = await getDidntClockOutRolesRequest({
        companyId: selectedCompanyId,
        payload: {
          offset: 0,
          limit: 100,
          time_period: {
            gte: dayjs(dateOfTimeLog).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
            lte: dayjs(dateOfTimeLog).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
          },
        },
      });
      if (!isMounted.current) return;
      setRoleOptions(data.items.map(item => ({ id: item, name: item })));
    } catch (e) {
      handleWidgetError(e);
    }
  };

  const getMembers = async (offset: number = 0, signal?: AbortSignal) => {
    if (!selectedCompanyId || !isTabFocused) {
      return;
    }

    const itemStatusMap: Record<number, boolean> = setInitialLoadingStatusesFalse(offset, GRID_PAGE_SIZE);

    setLoadingTrue();
    try {
      const { data } = await getDidntClockedOutWorkersRequest({
        companyId: selectedCompanyId,
        payload: {
          offset: offset,
          limit: GRID_PAGE_SIZE,
          order_by: formatTimeSortModel<CheckOutWorkerDTO>(sortModel),
          time_period: {
            gte: dayjs(dateOfTimeLog).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
            lte: dayjs(dateOfTimeLog).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
          },
          worker_substring: substring,
          site_ids: getLocationsForRequest(selectedLocationOptions.length ? 'selected' : 'all'),
          ...filters,
        },
        signal,
      });

      const transformedData = data.items.map(item => ({
        ...item,
        checked_out: formatDate(item.checked_out, 'YYYY-MM-DD'),
      }));
      setTotal(data.count);
      setNewData({ newData: transformedData, offset });

      setInitialLoadingStatusesTrue({ offset, pageSize: GRID_PAGE_SIZE, itemStatusMap });
      setLoadingFalse();
    } catch (e) {
      const err = e as AxiosError;
      if (err.name !== 'CanceledError') {
        handleWidgetError(err);
        setLoadingFalse();
      }
    }
  };
  const getSharedFile = async () => {
    if (!selectedCompanyId) {
      return;
    }
    try {
      setDownloadButtonLoadingTrue();
      const res = await getCompaniesWorkersWidgetSharedData({
        companyId: selectedCompanyId,
        target: 'did_not_clock_out',
        payload: {
          offset: 0,
          limit: GRID_PAGE_SIZE,
          order_by: formatTimeSortModel<CheckOutWorkerDTO>(sortModel),
          time_period: {
            gte: dayjs(dateOfTimeLog).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
            lte: dayjs(dateOfTimeLog).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
          },
          worker_substring: substring,
          ...filters,
        },
      });
      generateExcelFileLink(res);
    } catch (e) {
      handleServerError(e);
    } finally {
      if (isMounted.current) setDownloadButtonLoadingFalse();
    }
  };
  const handleChangeFilter = (data: { roles: Record<string, boolean> }) => {
    const filteredRoles = Object.keys(data.roles).filter(
      item => data.roles[item] || data.roles[item] === undefined
    );
    setFilters({ roles: filteredRoles });
  };

  const onSubmit = async () => {
    const data = methods.getValues();
    handleChangeFilter(data);
  };

  const handleEditButtonClick = (worker: CheckOutWorkerDTO) => {
    getTimeLogsByShift({
      userId: worker.user_id,
      date: formatDate(dateOfTimeLog, 'YYYY-MM-DD'),
      site_id: worker.site_id,
    });
    setSelectedWorker(worker);
  };

  const handlePickTheNextWorker = () => {
    const index = data.findIndex(item => item.user_id === selectedWorker?.user_id);
    if (data[index + 1]) {
      handleEditButtonClick(data[index + 1]);
    } else {
      handleCloseSingleEditTimeLogsDialog();
    }
  };

  const handleDateToChange = (date: any) => {
    setDateOfTimeLog(formatDate(date, 'YYYY-MM-DD'));
  };

  const switchFromEditToExpand = () => {
    openExpandTimeLogsInformationDialog();
    closeEditSingleTimeLogDialog();
  };

  const handleCloseExpandTimeLogsInformationDialog = () => {
    closeExpandTimeLogsInformationDialog();
    setLogByShift(null);
    setSelectedWorker(null);
  };

  const handleCloseSingleEditTimeLogsDialog = () => {
    closeEditSingleTimeLogDialog();
    setLogByShift(null);
    setSelectedWorker(null);
  };

  const actionsAfterDeletingTimeLog = () => {
    handleCloseExpandTimeLogsInformationDialog();
    getMembers();
  };

  useInterval(getMembers, 30000);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    getMembers(0, signal);

    return () => {
      controller.abort();
    };
  }, [
    selectedCompanyId,
    sortModel,
    dateOfTimeLog,
    substring,
    filters,
    selectedLocationOptions,
    unselectedLocationOptions,
  ]);

  useEffect(() => {
    getRoles();
  }, [selectedCompanyId, dateOfTimeLog]);

  return {
    methods,
    workers: data,
    total,
    selectedWorker,
    loading,
    sortModel,
    cnaViewWorker,
    isMobile,
    dateOfTimeLog,
    logByShift,
    isEditSingleTimeLogDialogOpen,
    isExpandTimeLogsInformationDialog,
    isFilterDialogOpen,
    isSmallView,
    isShowSearchField,
    filterSettings,
    isFilterWasActivated,
    downloadButtonLoading,
    isLoadingList,
    resetLoadingList,
    getSharedFile,
    onSubmit,
    restoreFilters,
    openFilterDialog,
    closeFilterDialog,
    closeEditSingleTimeLogDialog,
    handlePageChange,
    handleSortModelChange,
    setSelectedWorker,
    handleSearchChange,
    handleDateToChange,
    handleEditButtonClick,
    switchFromEditToExpand,
    handleCloseExpandTimeLogsInformationDialog,
    actionsAfterDeletingTimeLog,
    getMembers,
    handlePickTheNextWorker,
    setSmallView,
    setShowSearchFieldTrue,
  };
};
